import axios from 'axios'

// Use relative URLs in production (empty string) for Netlify proxy
// Use local development URL when VUE_APP_API_BASE_URL is not set
const API_BASE_URL = process.env.NODE_ENV === 'production' 
  ? '/api'
  : 'http://localhost:7071/api'

class ApiService {
  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      timeout: 30000, // 30 second timeout
      headers: {
        'Content-Type': 'application/json'
      }
    })

    // Add auth token to requests (except for public endpoints)
    this.client.interceptors.request.use(config => {
      const token = localStorage.getItem('authToken')
      // Skip auth for public endpoints
      const isPublicEndpoint = config.url && (
        config.url.includes('/public/') || 
        config.url.includes('/menu-preview/')
      )
      
      if (token && !isPublicEndpoint) {
        config.headers.Authorization = `Bearer ${token}`
      }
      return config
    })

    // Handle auth errors
    this.client.interceptors.response.use(
      response => response,
      error => {
        if (error.response?.status === 401) {
          localStorage.removeItem('authToken')
          localStorage.removeItem('user')
          window.location.href = '/login'
        }
        return Promise.reject(error)
      }
    )
  }

  // Authentication
  async login(email, password) {
    try {
      console.log('Making login request to:', `${API_BASE_URL}/auth/login`)
      
      const response = await this.client.post('/auth/login', { email, password })
      console.log('Login response:', response)
      
      if (response.data.Success) {
        return response.data.Data
      } else {
        throw new Error(response.data.Message || 'Login failed')
      }
    } catch (error) {
      console.error('Login error details:', error)
      
      if (error.code === 'ECONNREFUSED' || error.message.includes('Network Error')) {
        throw new Error('Cannot connect to API server. Please make sure the backend is running on http://localhost:7071')
      }
      
      throw new Error(error.response?.data?.Message || error.message || 'Login failed')
    }
  }

  async register(userData) {
    try {
      console.log('Making registration request to:', `${API_BASE_URL}/auth/register`)
      console.log('Request data:', userData)
      
      const response = await this.client.post('/auth/register', userData)
      console.log('Registration response:', response)
      
      if (response.data.Success) {
        return response.data.Data
      } else {
        throw new Error(response.data.Message || 'Registration failed')
      }
    } catch (error) {
      console.error('Registration error details:', error)
      console.error('Error response:', error.response)
      console.error('Error response data:', error.response?.data)
      
      if (error.code === 'ECONNREFUSED' || error.message.includes('Network Error')) {
        throw new Error('Cannot connect to API server. Please make sure the backend is running on http://localhost:7071')
      }
      
      // Handle different types of errors
      if (error.response?.data?.Message) {
        throw new Error(error.response.data.Message)
      } else if (error.response?.data?.message) {
        throw new Error(error.response.data.message)
      } else if (error.message) {
        throw new Error(error.message)
      } else {
        throw new Error('Registration failed. Please try again.')
      }
    }
  }

  async forgotPassword(email) {
    try {
      console.log('Making forgot password request to:', `${API_BASE_URL}/auth/forgot-password`)
      
      const response = await this.client.post('/auth/forgot-password', { email })
      console.log('Forgot password response:', response)
      
      if (response.data.Success) {
        return response.data
      } else {
        throw new Error(response.data.Message || 'Failed to send reset email')
      }
    } catch (error) {
      console.error('Forgot password error details:', error)
      
      if (error.code === 'ECONNREFUSED' || error.message.includes('Network Error')) {
        throw new Error('Cannot connect to API server. Please make sure the backend is running on http://localhost:7071')
      }
      
      throw new Error(error.response?.data?.Message || error.message || 'Failed to send reset email')
    }
  }

  async resetPassword(resetData) {
    try {
      console.log('Making reset password request to:', `${API_BASE_URL}/auth/reset-password`)
      
      const response = await this.client.post('/auth/reset-password', resetData)
      console.log('Reset password response:', response)
      
      if (response.data.Success) {
        return response.data
      } else {
        throw new Error(response.data.Message || 'Failed to reset password')
      }
    } catch (error) {
      console.error('Reset password error details:', error)
      
      if (error.code === 'ECONNREFUSED' || error.message.includes('Network Error')) {
        throw new Error('Cannot connect to API server. Please make sure the backend is running on http://localhost:7071')
      }
      
      throw new Error(error.response?.data?.Message || error.message || 'Failed to reset password')
    }
  }

  // Locations
  async getLocations() {
    try {
      const response = await this.client.get('/locations')
      if (response.data.Success) {
        return response.data.Data
      } else {
        throw new Error(response.data.Message || 'Failed to fetch locations')
      }
    } catch (error) {
      throw new Error(error.response?.data?.Message || 'Failed to fetch locations')
    }
  }

  async createLocation(locationData) {
    try {
      const response = await this.client.post('/locations', locationData)
      if (response.data.Success) {
        return response.data.Data
      } else {
        throw new Error(response.data.Message || 'Failed to create location')
      }
    } catch (error) {
      throw new Error(error.response?.data?.Message || 'Failed to create location')
    }
  }

  async updateLocation(id, locationData) {
    try {
      const response = await this.client.put(`/locations/${id}`, locationData)
      if (response.data.Success) {
        return response.data.Data
      } else {
        throw new Error(response.data.Message || 'Failed to update location')
      }
    } catch (error) {
      throw new Error(error.response?.data?.Message || 'Failed to update location')
    }
  }

  async deleteLocation(id) {
    try {
      const response = await this.client.delete(`/locations/${id}`)
      if (response.data.Success) {
        return response.data.Data
      } else {
        throw new Error(response.data.Message || 'Failed to delete location')
      }
    } catch (error) {
      throw new Error(error.response?.data?.Message || 'Failed to delete location')
    }
  }

  // Categories
  async getCategories() {
    try {
      const response = await this.client.get('/categories')
      if (response.data.success) {
        return response.data.data
      } else {
        throw new Error(response.data.message || 'Failed to fetch categories')
      }
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch categories')
    }
  }

  async createCategory(categoryData) {
    try {
      const response = await this.client.post('/categories', categoryData)
      if (response.data.success) {
        return response.data.data
      } else {
        throw new Error(response.data.message || 'Failed to create category')
      }
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to create category')
    }
  }

  async updateCategory(id, categoryData) {
    try {
      const response = await this.client.put(`/categories/${id}`, categoryData)
      if (response.data.success) {
        return response.data.data
      } else {
        throw new Error(response.data.message || 'Failed to update category')
      }
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to update category')
    }
  }

  async deleteCategory(id) {
    try {
      const response = await this.client.delete(`/categories/${id}`)
      if (response.data.success) {
        return response.data.data
      } else {
        throw new Error(response.data.message || 'Failed to delete category')
      }
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to delete category')
    }
  }

  // Single Location
  async getLocation(id) {
    try {
      const response = await this.client.get(`/locations/${id}`)
      if (response.data.Success) {
        return response.data.Data
      } else {
        throw new Error(response.data.Message || 'Failed to fetch location')
      }
    } catch (error) {
      throw new Error(error.response?.data?.Message || 'Failed to fetch location')
    }
  }

  // Menus (independent of location)
  async getMenus() {
    try {
      const response = await this.client.get('/menus')
      if (response.data.success) {
        return response.data.data
      } else {
        throw new Error(response.data.message || 'Failed to fetch menus')
      }
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch menus')
    }
  }

  async getMenu(menuId) {
    try {
      const response = await this.client.get(`/menus/${menuId}`)
      if (response.data.success) {
        return response.data.data
      } else {
        throw new Error(response.data.message || 'Failed to fetch menu')
      }
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch menu')
    }
  }

  async createMenu(menuData) {
    try {
      const response = await this.client.post('/menus', menuData)
      if (response.data.success) {
        return response.data.data
      } else {
        throw new Error(response.data.message || 'Failed to create menu')
      }
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to create menu')
    }
  }

  async updateMenu(menuId, menuData) {
    try {
      const response = await this.client.put(`/menus/${menuId}`, menuData)
      if (response.data.success) {
        return response.data.data
      } else {
        throw new Error(response.data.message || 'Failed to update menu')
      }
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to update menu')
    }
  }

  async deleteMenu(menuId) {
    try {
      const response = await this.client.delete(`/menus/${menuId}`)
      if (response.data.success) {
        return response.data.data
      } else {
        throw new Error(response.data.message || 'Failed to delete menu')
      }
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to delete menu')
    }
  }

  // Update location's active menu
  async updateLocationActiveMenu(locationId, menuId) {
    try {
      const response = await this.client.put(`/locations/${locationId}/active-menu`, { menuId })
      if (response.data.Success) {
        return response.data.Data
      } else {
        throw new Error(response.data.Message || 'Failed to update location active menu')
      }
    } catch (error) {
      throw new Error(error.response?.data?.Message || 'Failed to update location active menu')
    }
  }

  // Menu Items (now belong to menus, not locations)
  async getMenuItems(menuId) {
    try {
      const response = await this.client.get(`/menus/${menuId}/menu-items`)
      if (response.data.success) {
        return response.data.data
      } else {
        throw new Error(response.data.message || 'Failed to fetch menu items')
      }
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch menu items')
    }
  }

  async createMenuItem(menuId, itemData) {
    try {
      const response = await this.client.post(`/menus/${menuId}/menu-items`, itemData)
      if (response.data.success) {
        return response.data.data
      } else {
        throw new Error(response.data.message || 'Failed to create menu item')
      }
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to create menu item')
    }
  }

  async updateMenuItem(menuId, itemId, itemData) {
    try {
      const response = await this.client.put(`/menus/${menuId}/menu-items/${itemId}`, itemData)
      if (response.data.success) {
        return response.data.data
      } else {
        throw new Error(response.data.message || 'Failed to update menu item')
      }
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to update menu item')
    }
  }

  async deleteMenuItem(menuId, itemId) {
    try {
      const response = await this.client.delete(`/menus/${menuId}/menu-items/${itemId}`)
      if (response.data.success) {
        return response.data.data
      } else {
        throw new Error(response.data.message || 'Failed to delete menu item')
      }
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to delete menu item')
    }
  }

  // QR Codes
  async generateQRCode(locationId) {
    try {
      const response = await this.client.get(`/qrcodes/${locationId}`)
      if (response.data.Success) {
        return response.data.Data
      } else {
        throw new Error(response.data.Message || 'Failed to generate QR code')
      }
    } catch (error) {
      throw new Error(error.response?.data?.Message || 'Failed to generate QR code')
    }
  }

  async downloadQRCode(locationId) {
    try {
      const response = await this.client.get(`/qrcodes/${locationId}/download`, {
        responseType: 'blob'
      })
      return response.data
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to download QR code')
    }
  }

  // Public Menu API (no authentication required)
  async getPublicMenu(locationId) {
    try {
      console.log(`Fetching public menu for location: ${locationId}`)
      const startTime = Date.now()
      
      const response = await this.client.get(`/public/menu/${locationId}`)
      
      const endTime = Date.now()
      console.log(`Public menu request completed in ${endTime - startTime}ms`)
      
      if (response.data.success) {
        return response.data
      } else {
        throw new Error(response.data.message || 'Failed to load menu')
      }
    } catch (error) {
      console.error('Public menu request failed:', error)
      if (error.code === 'ECONNABORTED') {
        throw new Error('Request timeout - the server is taking too long to respond')
      }
      throw new Error(error.response?.data?.message || 'Failed to load menu')
    }
  }

  // Location Menu Management
  async updateLocationActiveMenu(locationId, menuId) {
    try {
      const response = await this.client.put(`/locations/${locationId}/active-menu`, { menuId })
      if (response.data.success) {
        return response.data.data
      } else {
        throw new Error(response.data.message || 'Failed to update location menu')
      }
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to update location menu')
    }
  }

  // Menu Preview (for menu management)
  async getMenuPreview(menuId) {
    try {
      console.log(`Fetching menu preview for menu: ${menuId}`)
      const startTime = Date.now()
      
      const response = await this.client.get(`/menu-preview/${menuId}`)
      
      const endTime = Date.now()
      console.log(`Menu preview request completed in ${endTime - startTime}ms`)
      
      if (response.data.success) {
        return response.data
      } else {
        throw new Error(response.data.message || 'Failed to load menu preview')
      }
    } catch (error) {
      console.error('Menu preview request failed:', error)
      if (error.code === 'ECONNABORTED') {
        throw new Error('Request timeout - the server is taking too long to respond')
      }
      throw new Error(error.response?.data?.message || 'Failed to load menu preview')
    }
  }

  // QR Code PDF Generation
  async generateQRCodesPdf(locationId, numberOfQRCodes, qrCodesPerPage) {
    try {
      console.log(`Generating QR codes PDF for location: ${locationId}`)
      
      const response = await this.client.post('/qrcodes/pdf', {
        locationId,
        numberOfQRCodes,
        qrCodesPerPage
      }, {
        responseType: 'blob',
        timeout: 60000 // 60 second timeout for PDF generation
      })
      
      // Validate the response
      if (!response.data || response.data.size === 0) {
        throw new Error('Empty PDF response received')
      }
      
      // Check if the response is actually a PDF
      if (response.data.type && !response.data.type.includes('pdf')) {
        // If it's not a PDF, it might be an error response in JSON format
        const text = await response.data.text()
        try {
          const errorData = JSON.parse(text)
          throw new Error(errorData.message || 'Invalid response format')
        } catch {
          throw new Error('Invalid PDF response format')
        }
      }
      
      return response.data
    } catch (error) {
      console.error('QR codes PDF generation failed:', error)
      if (error.response?.data) {
        // Try to read the error response
        try {
          const text = await error.response.data.text()
          const errorData = JSON.parse(text)
          throw new Error(errorData.message || 'Failed to generate QR codes PDF')
        } catch {
          throw new Error('Failed to generate QR codes PDF')
        }
      }
      throw new Error(error.message || 'Failed to generate QR codes PDF')
    }
  }
}

export const apiService = new ApiService()
export default apiService
